import {colors} from "@c10h/colors";
/* eslint-disable react-hooks/rules-of-hooks */
// @ts-expect-error TS7016: Could not find a declaration file for module 'color'. '/Users/goksel/Sites/carbon-website-next/node_modules/color/index.js' implicitly has an 'any' type.
import Color from "color";
import Link from "next/link";
import {useTranslation} from "ni18n";
import React, {useEffect, useState} from "react";
import {useTypedSelector} from "src/store";
import {RootStateLocation} from "src/store/types";

import {PracticeId} from "../../constants/practiceIds";
import {SpecialtyId} from "../../constants/specialtyIds";
import {
  useGetSoonestInPracticeBySpecialtyId,
  useGetSoonestTimeByLocationsAndSpecialtyIds,
} from "../../hooks/useGetSoonestTime";
import {getSchedulingRedirectUrl} from "../../utils/getRedirectUrl";
import {
  getVirtualSpecialtyIds,
  todayTomorrow2,
  trackToPatient,
  useInterval,
} from "../_common/Carbon";
import NextImage from "./NextImage";
import styles from "./Services.module.scss";
import {Btn} from "./V5Comp";

const time = 5000;
const timeSec = `${time / 1000}s`;

export type ServicesSlide = {
  head: string;
  text: string;
  color: string;
  img: string;
  isNew?: boolean;
  btn1?: any;
  btn2?: any;
  specialtyId?: SpecialtyId;
  btnColor?: string;
  btnBgColor?: string;
  btnText?: string;
  href?: string;
  location?: RootStateLocation;
};

export type ServicesPropsType = {
  noBtns?: true;
  header?: string;
  reverse?: true;
  customSlides?: ServicesSlide[];
  innerStyle?: any;
  practiceId?: string;
};

/**
 * @param noBtns When true, buttons will not be rendered for each service and no API calls will be made to retrieve appointment slots.
 * If you pass the `noBtns` prop, you should not pass `locations`, `practices`, or `selectedRegion`,
 * Otherwise, these are required.
 * @param reverse If true, places the images on the left and services on the right
 * @param locationsSorted (IF DEFAULT SLIDES) Used to determined if scheduling redirect url uses preselected location ids
 * @param practices  (IF DEFAULT SLIDES) getVirtualSpecialtyIds() will be called on practices for scheduling redirect url
 */

const ServicesTimedCarousel = ({
  header,
  customSlides,
  noBtns,
  reverse,
  innerStyle,
  practiceId,
}: ServicesPropsType) => {
  const i18n = useTranslation();
  const {isMobile, practices, locationsSorted, locations} = useTypedSelector(({config}) => config);

  const defaultSlides: ServicesSlide[] = [
    {
      specialtyId: SpecialtyId.PRIMARY_CARE,
      head: i18n.t("Primary Care"),
      text: i18n.t(
        "From preventive care to chronic issues, our primary care providers partner with you for all your healthcare needs.",
      ),
      color: "#58B3CA",
      btnColor: "#fff",
      btnBgColor: colors.blue,
      btnText: i18n.t("Book appointment"),
      img: "services_caro0",
      href: getSchedulingRedirectUrl({
        practiceId: practiceId || PracticeId.CARBON,
        specialtyId: SpecialtyId.PRIMARY_CARE,
      }),
    },
    {
      specialtyId: SpecialtyId.URGENT_CARE,
      head: i18n.t("Urgent Care"),
      text: i18n.t(
        "Fast, same-day care for illnesses and injuries from sudden fevers and stomach upsets to cuts, rashes and sprains.",
      ),
      color: colors.yellow,
      btnColor: colors.gray800,
      btnBgColor: colors.yellow,
      btnText: i18n.t("Book appointment"),
      img: "services_caro1",
      href: getSchedulingRedirectUrl({
        practiceId: practiceId || PracticeId.CARBON,
        specialtyId: SpecialtyId.URGENT_CARE,
      }),
    },
    {
      head: i18n.t("Virtual Care"),
      text: i18n.t(
        "Virtual primary care, urgent care, and mental health visits - all delivered online from the comfort of your home.",
      ),
      color: colors.mint,
      btnColor: colors.gray800,
      btnBgColor: colors.mint,
      btnText: i18n.t("Start"),
      img: "services_caro2",
      href: getSchedulingRedirectUrl({
        practiceId: practiceId || PracticeId.CARBON,
        virtual: true,
        // @ts-expect-error TS2345: Argument of type 'RootStatePractice[] | undefined' is not assignable to parameter of type 'RootStatePractice[]'.
        specialtyId: getVirtualSpecialtyIds(practices),
      }),
    },
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const [delay, setDelay] = useState(time);
  const [pause, setPause] = useState(true);
  const [slides, setSlides] = useState(customSlides || defaultSlides);

  const startTime = Date.now();

  const activeSlide = slides[activeIndex];

  const slotsData = useGetSoonestTimeByLocationsAndSpecialtyIds({
    // @ts-expect-error TS2322: Type '(SpecialtyId | undefined)[]' is not assignable to type 'SpecialtyId[]'.
    specialtyIds: slides.map(s => s.specialtyId),
  });

  const closesetLocationsWithPrimaryCare = useGetSoonestInPracticeBySpecialtyId({
    specialtyId: SpecialtyId.PRIMARY_CARE,
  });
  const closesetLocationsWithUrgentCare = useGetSoonestInPracticeBySpecialtyId({
    specialtyId: SpecialtyId.URGENT_CARE,
  });

  useEffect(() => {
    if (!locationsSorted || noBtns) return;
    setPause(false);

    const newSlides = slides.map(s => {
      const slide: ServicesSlide = s;
      if (slide.specialtyId === SpecialtyId.PRIMARY_CARE)
        // @ts-expect-error TS2532, TS2684: Object is possibly 'undefined'.,  The 'this' context of type 'RootStateLocation[] | undefined' is not assignable to method's 'this' of type 'RootStateLocation[]'.
        slide.location = locations.findById(closesetLocationsWithUrgentCare.locationId);
      else if (slide.specialtyId === SpecialtyId.URGENT_CARE)
        // @ts-expect-error TS2532, TS2684: Object is possibly 'undefined'.,  The 'this' context of type 'RootStateLocation[] | undefined' is not assignable to method's 'this' of type 'RootStateLocation[]'.
        slide.location = locations.findById(closesetLocationsWithPrimaryCare.locationId);
      return slide;
    });

    setSlides(newSlides);
  }, [locationsSorted]);

  if (!isMobile) {
    useInterval(
      () => {
        setActiveIndex((activeIndex + 1) % slides.length);
      },
      delay,
      pause,
    );
  }

  useEffect(() => {
    setDelay(time);
  }, [activeIndex]);

  const images = (
    <div className="pos-r desktopOnly">
      {slides.map((s, i) => (
        <div key={s.head} className={`${styles.rightImg} ${i === activeIndex ? "on" : ""}`}>
          <NextImage src={s.img} layout="fill" objectFit="cover" objectPosition="0 75%" alt="" />
        </div>
      ))}
    </div>
  );

  const renderContent = () => (
    <div className={`${styles.wrap} dg gac bg-smoke pos-r`} id="servicesWrap">
      {reverse && images}
      <div className={`${styles.inner} tac`}>
        <div className={`${styles.leftInner}`} style={innerStyle}>
          <h5 className={`${styles.header} gray400`}>{header || i18n.t("Our Services")}</h5>
          <div className="mt4 dn db-md tac">
            <NextImage src={activeSlide.img} height={400} width={400 * 0.8} alt="" />
          </div>
          <div
            className={styles.rows}
            onMouseEnter={() => {
              const timeLeft = delay - (Date.now() - startTime);
              setDelay(timeLeft);
              setPause(true);
            }}
            onMouseLeave={() => {
              setPause(false);
            }}
          >
            {slides.map(
              (
                {
                  location,
                  specialtyId,
                  head,
                  text,
                  color,
                  btnColor,
                  btnBgColor,
                  btnText,
                  isNew,
                  href,
                  btn1,
                  btn2,
                },
                i,
              ) => {
                // @ts-expect-error TS2538: Type 'undefined' cannot be used as an index type.
                const firstSlot = !noBtns ? slotsData[specialtyId].vals().min() : undefined;
                return (
                  <div key={head} className={`${styles.row} ${activeIndex === i ? "active" : ""}`}>
                    {locationsSorted && (
                      <div className={styles.rowSpinner}>
                        <div className="spinerContainer">
                          <div
                            className="pie spinner"
                            style={{
                              "--bg": color,
                              "--time": timeSec,
                              "--state": pause ? "paused" : "running",
                            }}
                          />
                          <div
                            className="pie filler"
                            style={{
                              "--bg": color,
                              "--time": timeSec,
                              "--state": pause ? "paused" : "running",
                            }}
                          />
                          <div
                            className="mask"
                            style={{"--time": timeSec, "--state": pause ? "paused" : "running"}}
                          />
                        </div>
                      </div>
                    )}

                    <div className={`${styles.rowHeadContainer} df aic`}>
                      <h2 className={`${styles.rowHead} fs24-md`} onClick={() => setActiveIndex(i)}>
                        {head}
                      </h2>
                      {isNew && <span className={`${styles.new} newLabel`}>{i18n.t("NEW")}</span>}
                    </div>
                    <div className={`${styles.rowBody}`}>
                      <div className="color-nevada fs20 lh12 mt4 lh15 font-ir fs16-md">{text}</div>

                      {!noBtns && (
                        <div>
                          <div className="dig aic mt4 gg8 gg4-md">
                            {location ? (
                              // @ts-expect-error TS2322: Type 'string | undefined' is not assignable to type 'Url'.
                              <Link href={href}>
                                <button
                                  type="button"
                                  className={`${styles.btn} btn bold-f`}
                                  style={{
                                    "--color": btnColor,
                                    "--btnBgColor": btnBgColor || color,
                                    "--btnHoverBg": Color(btnBgColor || color).darken(0.2),
                                  }}
                                >
                                  {btnText}
                                </button>
                              </Link>
                            ) : (
                              locationsSorted && (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => trackToPatient()}
                                >
                                  <button
                                    type="button"
                                    className={`${styles.btn} btn bold-f`}
                                    style={{
                                      "--color": btnColor,
                                      "--btnBgColor": btnBgColor || color,
                                      "--btnHoverBg": Color(btnBgColor || color).darken(0.2),
                                    }}
                                  >
                                    {btnText}
                                  </button>
                                </a>
                              )
                            )}

                            {firstSlot && specialtyId && (
                              <div className="fs14 color-nevada ">
                                <span className="desktopOnly">{i18n.t("Next at")}</span>
                                <span className="color-default medium ml1">
                                  {todayTomorrow2(i18n.lang, firstSlot)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {(btn1 || btn2) && (
                        <div>
                          <div className="dig aic mt4 gg2 gg4-md">
                            {btn1 && (
                              <Btn
                                color={btn1.color}
                                bg={btn1.bgColor}
                                href={btn1.url}
                                fontSize="12px"
                              >
                                {btn1.title}
                              </Btn>
                            )}
                            {btn2 && (
                              <Btn
                                color={btn2.color}
                                bg={btn2.bgColor}
                                href={btn2.url}
                                fontSize="12px"
                              >
                                {btn2.title}
                              </Btn>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>
      </div>
      {!reverse && images}
    </div>
  );

  return renderContent();
};

export default ServicesTimedCarousel;
